<template>
  <BaseDialog
    contentClass="tenant"
    ref="dialog"
    :show-actions="true"
    :persistent="isDirty"
    @closeModal="closeDialog"
    sidePanel
  >
    <template slot="title">{{ $t('page.admin-tenants.new-tenant') }}</template>
    <template slot="content">
      <form @submit.prevent="onSubmit" novalidate class="new-tenant__form">
        <div class="login-form__section-small">
          <FormInput
            :label="$t('page.admin-tenants.data.name')"
            has-gray-background
            :error="nameError"
            hint="Ce nom doit être unique"
            name="name"
            type="name"
            ref="name"
            v-model="name"
            v-validate="'required'"
            @input="onChange"
          />
        </div>

        <div class="login-form__section-small">
          <BaseSelect
            :label="$t('page.admin-tenants.data.subscription')"
            name="subscription"
            :value="subscriptionType"
            :items="subscriptions"
            item-text="text"
            item-value="value"
            :hint="$t('page.admin-tenants.select-subscription')"
            @change="onSubscriptionChange"
          />
        </div>
        <div class="login-form__section-small">
          <FormDateRangePicker
            ref="trialDates"
            name="trial"
            :label="$t('page.admin-tenants.data.trial-dates')"
            :values="trialDates"
            :error="trialDatesError"
            v-validate="'length:2'"
            v-model="trialDates"
            @updated-dates="updateDates"
          />
        </div>

        <div class="admin-creation">
          <div class="admin-creation--title">
            <BaseIcon name="user" />
            <span class="admin-creation--text">{{ $t('page.admin-tenants.admin-creation') }}</span>
          </div>

          <div class="login-form__section-small tenant-email-wrapper">
            <FormInput
              class="tenant-email"
              :error="emailError"
              label="Adresse courriel"
              has-gray-background
              name="email"
              type="email"
              ref="email"
              v-model="email"
              v-validate="'required'"
              @input="onChange"
            />
            <span class="tenant-email-suffix">{{ emailSuffix }}</span>
          </div>

          <div class="login-form__section-small">
            <FormInput
              :error="passwordError"
              label="Mot de passe"
              has-gray-background
              name="password"
              :type="passwordType"
              ref="password"
              v-model="password"
              v-validate="'required'"
              @input="onChange"
              autocomplete="on"
            >
              <template #top-right>
                <button
                  class="reset-password-form__toggle-password"
                  type="button"
                  @click.prevent="togglePassword"
                  tabindex="-1"
                >
                  <BaseIcon name="eye" v-if="passwordHidden" />
                  <BaseIcon name="eye-off" v-else />
                  {{ passwordHidden ? $t('reset-password.see') : $t('reset-password.hide') }}
                </button>
              </template>
            </FormInput>
            <BaseButton is-small @click.native="regeneratePassword" class="admin-creation__regenerate-password">
              <BaseIcon name="refresh-ccw" />
              {{ $t('page.admin-tenants.regenerate-password') }}
            </BaseButton>
          </div>
        </div>

        <div class="actions">
          <FormButton is-large :disabled="!isFormValid" :is-outlined="!isFormValid" type="submit">
            {{ $t('page.admin-tenants.new-tenant') }}
          </FormButton>
        </div>
      </form>
    </template>
  </BaseDialog>
</template>

<script>
import BaseButton from '@/components/base/BaseButton';
import BaseDialog from '@/components/base/BaseDialog';
import BaseIcon from '@/components/base/BaseIcon';
import BaseSelect from '@/components/base/BaseSelect';
import FormButton from '@/components/form/FormButton';
import FormDateRangePicker from '@/components/form/FormDateRangePicker';
import FormInput from '@/components/form/FormInput.vue';

import { mapActions } from 'vuex';

import { validatePassword, generatePassword } from '@/util/password-util';
import { SUBSCRIPTIONS, DEFAULT_SUBSCRIPTION } from '@/models/subscriptions';
import { toMoment } from '@/util/date-util';

export default {
  name: 'AddTenantDialog',
  components: {
    BaseButton,
    BaseDialog,
    BaseIcon,
    BaseSelect,
    FormButton,
    FormDateRangePicker,
    FormInput,
  },
  data() {
    return {
      isDirty: false,
      name: '',
      subscriptionType: '',
      trialDates: [],
      email: '',
      password: '',
      passwordHidden: true,
    };
  },
  computed: {
    subscriptions() {
      return SUBSCRIPTIONS.map((value) => ({
        text: this.$t(`subscriptions.${value}`),
        value,
      }));
    },
    isFormValid() {
      return (
        this.name &&
        this.email &&
        this.password &&
        this.passwordCriteria.valid &&
        (this.trialDates.length === 2 || this.trialDates.length === 0)
      );
    },
    nameError() {
      const error = this.errors.items.find((x) => x.field === 'name');

      return error ? error.msg : undefined;
    },
    emailError() {
      const error = this.errors.items.find((x) => x.field === 'email');

      return error ? error.msg : undefined;
    },
    trialDatesError() {
      const trialDateError = this.errors.items.find((x) => x.field === 'trial');

      return trialDateError ? trialDateError.msg : undefined;
    },
    passwordError() {
      const formError = this.errors.items.find((x) => x.field === 'password');
      return formError ? formError.msg : undefined;
    },
    passwordType() {
      return this.passwordHidden ? 'password' : 'text';
    },
    passwordCriteria() {
      return validatePassword(this.password);
    },
    emailSuffix() {
      return '@aloha.app';
    },
    emailBuild() {
      return this.name
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replaceAll(' ', '-')
        .toLowerCase();
    },
  },
  methods: {
    ...mapActions('tenants', ['createTenant']),
    toMoment,
    openDialog() {
      this.name = '';
      this.email = '';
      this.subscriptionType = DEFAULT_SUBSCRIPTION;
      this.password = generatePassword();

      this.$refs.dialog.openModal();

      this.$nextTick(() => {
        const refInput = this.$refs.name.$el.querySelector('input');
        if (refInput) {
          this.$nextTick(() => {
            refInput.focus();
          });
        }
      });
    },
    closeDialog() {
      this.$validator.reset();
      this.errors.clear();
      this.isDirty = false;
      this.$refs.trialDates.clearDatePickerDates();
      this.$refs.dialog.isModalOpen = false;
    },
    onChange() {
      this.isDirty = true;
    },
    updateDates(dates) {
      this.onChange();
      this.trialDates = dates;
    },
    regeneratePassword() {
      this.password = generatePassword();
    },
    togglePassword() {
      this.passwordHidden = !this.passwordHidden;
    },
    onSubscriptionChange(subscription) {
      this.onChange();
      this.subscriptionType = subscription;
    },
    formatEmail(email) {
      return email + this.emailSuffix;
    },
    onSubmit() {
      const tenant = {
        name: this.name,
        subscriptionType: this.subscriptionType.value,
        active: true,
        trialStartDate: toMoment(this.trialDates[0]).unix() * 1000,
        trialEndDate: toMoment(this.trialDates[1]).unix() * 1000,
        defaultUserEmail: this.formatEmail(this.email),
        defaultUserPassword: this.password,
      };
      this.createTenant(tenant)
        .then(() => {
          this.$toast.success(this.$t('page.admin-tenants.new-tenant-created'));
          this.$emit('action-succeeded');
          this.closeDialog();
        })
        .catch((error) => this.$toast.error(error));
    },
  },
  watch: {
    emailBuild() {
      this.email = this.emailBuild;
    },
  },
};
</script>

<style lang="scss">
.base-dialog__tenant {
  .base-dialog__content {
    padding: 32px;
    background: $color-white !important;
  }

  .actions {
    display: flex;
    justify-content: center;
    padding: 2rem;
  }

  .admin-creation {
    border: 1px solid $color-border;
    padding: 0 1rem;

    &--title {
      padding: 1rem 0;
      align-items: center;
      display: flex;
    }

    &--text {
      padding-left: 0.5rem;
      color: $color-normal;
      font-family: $font-family-raleway;
      font-size: 14px;
      font-stretch: normal;
      font-style: normal;
      font-weight: 800;
      letter-spacing: normal;
      line-height: 1.36;
    }

    &__regenerate-password {
      .base-icon {
        height: 15px;
        width: 15px;
        margin-right: 6px;
      }
    }
  }

  .tenant-email-wrapper {
    display: inline-block;
    position: relative;
    width: 100%;
  }

  .tenant-email-suffix {
    position: absolute;
    right: 12px;
    top: 35px;
    color: $color-normal-pale;
  }
}
</style>
